import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import {
  STATUS_LIST, STATUS_META, fmtEur, fmtDate, fmtDateTime, gewerkRu,
  ASSIGNMENT_STATUS, CONTRACT_STATUS, PAYMENT_STAGE_STATUS, INVOICE_STATUS,
  berlinLocalToUtcIso, isoToBerlinLocalInput, fmtBerlinDateTime,
} from "@/lib/domain";
import { StatusBadge, Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VoiceRecorder, VoicePlayer } from "@/components/Voice";
import { ArrowLeft, MapPin, Calendar, Camera, Send, MessageSquare, Trash2, User, TrendingUp, Sparkles, Loader2, Plus, X, Pencil, Wallet, FileSignature } from "lucide-react";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Photos are served from a private endpoint — each URL needs a short-lived,
// single-file-scoped token (fetched via /api/files/token/{path}), not the
// main access JWT. See photoTokens state below.
function rawPhotoPath(u) {
  if (!u) return null;
  if (u.startsWith("data:") || u.startsWith("http")) return null;
  const m = u.match(/^\/api\/files\/(.+)$/);
  return m ? m[1] : null;
}

export default function ObjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [obj, setObj] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [client, setClient] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newLog, setNewLog] = useState("");
  const [newMsg, setNewMsg] = useState("");
  const [tab, setTab] = useState("overview");
  const [photoTokens, setPhotoTokens] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [assignmentsError, setAssignmentsError] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignForm, setAssignForm] = useState({ worker_id: "", role: "рабочий", starts_at: "", ends_at: "" });
  const [commercial, setCommercial] = useState(null);
  const [commercialError, setCommercialError] = useState(null);

  const loadAssignments = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get(`/objects/${id}/assignments`);
      setAssignments(r.data);
      setAssignmentsError(null);
    } catch (e) {
      setAssignmentsError(formatApiErrorDetail(e.response?.data?.detail) || "Планирование работ недоступно");
    }
  };

  const authedUrl = (u) => {
    if (!u) return u;
    if (u.startsWith("data:") || u.startsWith("http")) return u;
    const path = rawPhotoPath(u);
    const abs = `${BACKEND_URL}${u}`;
    const token = path ? photoTokens[path] : null;
    return token ? `${abs}?auth=${encodeURIComponent(token)}` : abs;
  };

  const load = async () => {
    setLoadError(null);
    let o;
    try {
      o = await api.get(`/objects/${id}`);
      setObj(o.data);
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Не удалось загрузить объект");
      return;
    }
    if (isAdmin) {
      api.get("/workers")
        .then((r) => setWorkers(r.data))
        .catch(() => setWorkers([]));
    } else {
      setWorkers([]);
    }
    if (isAdmin) {
      try { const c = await api.get(`/clients/${o.data.client_id}`); setClient(c.data); } catch {}
    }
    api.get(`/objects/${id}/logs`).then((r) => setLogs(r.data));
    api.get(`/objects/${id}/chat`).then((r) => setMessages(r.data));

    const paths = (o.data.photos || []).map(rawPhotoPath).filter(Boolean);
    const tokenEntries = await Promise.all(
      paths.map(async (path) => {
        try {
          const r = await api.get(`/files/token/${path}`);
          return [path, r.data.token];
        } catch {
          return [path, null];
        }
      })
    );
    setPhotoTokens(Object.fromEntries(tokenEntries));
    if (isAdmin) {
      api.get(`/objects/${id}/commercial-overview`)
        .then((r) => { setCommercial(r.data); setCommercialError(null); })
        .catch((e) => setCommercialError(formatApiErrorDetail(e.response?.data?.detail) || "Коммерческий процесс недоступен"));
    }
  };

  useEffect(() => { load(); loadAssignments(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (status) => {
    await api.patch(`/objects/${id}`, { status });
    toast.success("Статус обновлён");
    load();
  };

  const addLog = async () => {
    if (!newLog.trim()) return;
    await api.post(`/objects/${id}/logs`, { text: newLog });
    setNewLog("");
    api.get(`/objects/${id}/logs`).then((r) => setLogs(r.data));
    toast.success("Запись добавлена");
  };

  const sendMsg = async () => {
    if (!newMsg.trim()) return;
    await api.post(`/objects/${id}/chat/text`, { text: newMsg });
    setNewMsg("");
    api.get(`/objects/${id}/chat`).then((r) => setMessages(r.data));
  };

  const sendVoice = async (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "voice.webm");
    toast.info("Отправка голосового…");
    await api.post(`/objects/${id}/chat/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    api.get(`/objects/${id}/chat`).then((r) => setMessages(r.data));
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    toast.info("Загрузка фото…");
    try {
      await api.post(`/objects/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
      toast.success("Фото добавлено");
    } catch (e) {
      toast.error("Ошибка загрузки: " + (e.response?.data?.detail || e.message));
    }
  };

  const [aiSummary, setAiSummary] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const summarize = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/summarize-logs", { object_id: id, days: 7 });
      setAiSummary(data);
      toast.success(`Резюме готово (записей: ${data.count})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI недоступен");
    } finally { setAiBusy(false); }
  };

  const startNewAssignment = () => {
    setEditingAssignment(null);
    setAssignForm({ worker_id: "", role: "рабочий", starts_at: "", ends_at: "" });
    setAssignDialogOpen(true);
  };

  const startEditAssignment = (a) => {
    setEditingAssignment(a);
    setAssignForm({
      worker_id: a.crm_worker?.id || "",
      role: a.role || "рабочий",
      starts_at: isoToBerlinLocalInput(a.starts_at),
      ends_at: isoToBerlinLocalInput(a.ends_at),
    });
    setAssignDialogOpen(true);
  };

  const saveAssignment = async () => {
    if (!assignForm.worker_id) return toast.error("Выберите сотрудника");
    if (!assignForm.role.trim()) return toast.error("Укажите роль");
    if (!assignForm.starts_at || !assignForm.ends_at) return toast.error("Укажите начало и окончание");
    const starts_at = berlinLocalToUtcIso(assignForm.starts_at);
    const ends_at = berlinLocalToUtcIso(assignForm.ends_at);
    try {
      if (editingAssignment) {
        await api.patch(`/assignments/${editingAssignment.id}`, {
          expected_version: editingAssignment.version,
          role: assignForm.role.trim(),
          starts_at, ends_at,
        });
        toast.success("Назначение обновлено");
      } else {
        await api.post(`/objects/${id}/assignments`, {
          worker_id: assignForm.worker_id, role: assignForm.role.trim(), starts_at, ends_at,
        });
        toast.success("Назначение создано");
      }
      setAssignDialogOpen(false);
      loadAssignments();
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const cancelAssignment = async (a) => {
    if (!window.confirm("Отменить назначение?")) return;
    try {
      await api.post(`/assignments/${a.id}/cancel`, { expected_version: a.version });
      toast.success("Назначение отменено");
      loadAssignments();
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeObject = async () => {
    if (!window.confirm("Удалить объект? Это действие нельзя отменить.")) return;
    await api.delete(`/objects/${id}`);
    nav("/objects");
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <button onClick={() => nav("/objects")} className="text-slate-400 hover:text-amber-400 text-sm flex items-center gap-1.5">
          <ArrowLeft size={14} /> К списку объектов
        </button>
        <Card className="p-8 bg-[#131924] border-red-500/30 text-center">
          <div className="text-red-300 mb-3">{loadError}</div>
          <Button onClick={load} variant="outline" className="bg-transparent border-[#212b3d] hover:bg-[#1c2434]">Повторить</Button>
        </Card>
      </div>
    );
  }
  if (!obj) return <div className="text-slate-400">Загрузка…</div>;

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="top-right" />
      <button data-testid="back-to-objects" onClick={() => nav("/objects")} className="text-slate-400 hover:text-amber-400 text-sm flex items-center gap-1.5">
        <ArrowLeft size={14} /> К списку объектов
      </button>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <StatusBadge status={obj.status} />
            {(obj.gewerke || []).map((g) => (
              <Chip key={g} className="bg-[#0f1521] text-slate-300 border-[#212b3d]">{gewerkRu(g)}</Chip>
            ))}
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight">{obj.title}</h1>
          <div className="flex items-center gap-4 text-sm text-slate-400 mt-2 flex-wrap">
            <span className="flex items-center gap-1.5"><MapPin size={14} /> {obj.postal_code} {obj.city}, {obj.address}</span>
            {obj.start_date && <span className="flex items-center gap-1.5"><Calendar size={14} /> {fmtDate(obj.start_date)} → {fmtDate(obj.end_date)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={obj.status} onValueChange={updateStatus}>
            <SelectTrigger data-testid="status-select" className="bg-[#131924] border-[#212b3d] w-52"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
              {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label_ru}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button data-testid="delete-object-button" variant="outline" size="icon"
              onClick={removeObject}
              className="bg-[#131924] border-[#212b3d] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40">
              <Trash2 size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Finance strip (admin) */}
      {isAdmin && obj.revenue_planned !== undefined && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox label="Бюджет" value={fmtEur(obj.budget)} />
          <StatBox label="Затраты" value={fmtEur(obj.actual_cost)} accent="red" />
          <StatBox label="Планируем." value={fmtEur(obj.revenue_planned)} accent="blue" />
          <StatBox label="Оборот" value={fmtEur(obj.revenue_actual)} accent="emerald" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#131924] border border-[#212b3d]">
          <TabsTrigger data-testid="tab-overview"  value="overview">Обзор</TabsTrigger>
          <TabsTrigger data-testid="tab-photos"    value="photos">Фото ({obj.photos?.length || 0})</TabsTrigger>
          <TabsTrigger data-testid="tab-log"       value="log">Журнал ({logs.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-chat"      value="chat">Чат ({messages.length})</TabsTrigger>
          {isAdmin && (
            <TabsTrigger data-testid="tab-assignments" value="assignments">Планирование работ ({assignments.length})</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger data-testid="tab-commercial" value="commercial">Vertrag & Zahlung</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="bg-[#131924] border-[#212b3d] p-5 lg:col-span-2">
            <h3 className="font-display font-semibold text-lg mb-4">Информация</h3>
            {client && (
              <div className="mb-4 pb-4 border-b border-[#212b3d]">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Клиент</div>
                <div className="font-medium">{client.company || client.name}</div>
                <div className="text-sm text-slate-400">{client.email} · {client.phone}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <InfoRow k="Адрес" v={`${obj.postal_code || ""} ${obj.city || ""}, ${obj.address || ""}`} />
              <InfoRow k="Статус" v={STATUS_META[obj.status]?.label_ru || obj.status} />
              <InfoRow k="Старт" v={fmtDate(obj.start_date)} />
              <InfoRow k="Финиш" v={fmtDate(obj.end_date)} />
              <InfoRow k="Создан" v={fmtDateTime(obj.created_at)} />
            </div>
          </Card>
          <Card className="bg-[#131924] border-[#212b3d] p-5">
            <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><User size={16} className="text-amber-400" /> Бригада</h3>
            <div className="space-y-2">
              {(obj.assigned_worker_ids || []).length === 0 && <div className="text-sm text-slate-500">Мастера не назначены</div>}
              {(obj.assigned_worker_ids || []).map((wid) => {
                const w = workers.find((x) => x.id === wid);
                return (
                  <div key={wid} className="flex items-center gap-3 p-2 rounded-md bg-[#0f1521]">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-300 text-xs font-semibold">
                      {(w?.name || "?")[0]}
                    </div>
                    <div className="text-sm">
                      <div>{w?.name || wid.slice(-6)}</div>
                      <div className="text-[11px] text-slate-500">{w?.phone}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="photos" className="mt-6">
          <div className="flex justify-end mb-4">
            <label className="cursor-pointer">
              <input data-testid="upload-photo-input" type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
              <span className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                <Camera size={14} /> Добавить фото
              </span>
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(obj.photos || []).map((src, i) => (
              <a key={i} href={authedUrl(src)} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden border border-[#212b3d] hover:border-amber-500/40 transition">
                <img src={authedUrl(src)} alt="" className="w-full h-full object-cover" />
              </a>
            ))}
            {(obj.photos || []).length === 0 && (
              <div className="col-span-full text-center py-10 text-slate-500">Фотографий пока нет</div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="log" className="mt-6 space-y-4">
          <div className="flex justify-end">
            <Button
              data-testid="ai-summarize-logs-button" size="sm" onClick={summarize} disabled={aiBusy}
              className="bg-purple-500/15 text-purple-300 border border-purple-500/30 hover:bg-purple-500/25">
              {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              AI: резюме за неделю
            </Button>
          </div>
          {aiSummary && (
            <Card className="bg-purple-500/5 border-purple-500/30 p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-purple-300 font-medium mb-2">
                <Sparkles size={12} /> Claude AI · резюме за 7 дней · {aiSummary.count} записей
              </div>
              <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{aiSummary.text}</div>
            </Card>
          )}
          <Card className="bg-[#131924] border-[#212b3d] p-4">
            <Textarea data-testid="new-log-input" value={newLog} onChange={(e) => setNewLog(e.target.value)}
              placeholder="Заметка от бригады: что сделано, что нужно, что заказать…"
              className="bg-[#0f1521] border-[#212b3d] min-h-[80px]" />
            <div className="flex justify-end mt-3">
              <Button data-testid="add-log-button" onClick={addLog} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Записать</Button>
            </div>
          </Card>
          <div className="space-y-2">
            {logs.map((l) => (
              <div key={l.id} className="rounded-lg border border-[#212b3d] bg-[#131924] p-4">
                <div className="text-[11px] text-slate-500 mb-1.5">{l.author_name} · {fmtDateTime(l.created_at)}</div>
                <div className="text-sm text-slate-200 whitespace-pre-wrap">{l.text}</div>
              </div>
            ))}
            {logs.length === 0 && <div className="text-center py-10 text-slate-500">В журнале пока пусто</div>}
          </div>
        </TabsContent>

        <TabsContent value="chat" className="mt-6">
          <Card className="bg-[#131924] border-[#212b3d] flex flex-col h-[600px]">
            <div className="p-3 border-b border-[#212b3d] flex items-center gap-2 text-sm text-slate-400">
              <MessageSquare size={16} className="text-amber-400" /> Чат бригады по объекту
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && <div className="text-center py-10 text-slate-500">Пока нет сообщений</div>}
              {messages.map((m) => {
                const mine = m.author_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[85%]">
                      {!mine && <div className="text-[11px] text-slate-500 mb-1 ml-1">{m.author_name}</div>}
                      {m.type === "text" ? (
                        <div className={`px-3.5 py-2 rounded-2xl text-sm ${mine ? "bg-amber-500 text-slate-900 rounded-br-sm" : "bg-[#1c2434] text-slate-100 rounded-bl-sm"}`}>
                          {m.text}
                        </div>
                      ) : (
                        <VoicePlayer src={m.audio_data_url} transcript={m.transcript} />
                      )}
                      <div className={`text-[10px] text-slate-500 mt-1 ${mine ? "text-right" : "text-left"}`}>{fmtDateTime(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-[#212b3d] flex items-center gap-2">
              <VoiceRecorder onSend={sendVoice} />
              <Input
                data-testid="chat-input"
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMsg()}
                placeholder="Сообщение…"
                className="bg-[#0f1521] border-[#212b3d]"
              />
              <Button data-testid="send-chat-button" onClick={sendMsg} size="icon" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                <Send size={16} />
              </Button>
            </div>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="assignments" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Button data-testid="new-assignment-button" size="sm" onClick={startNewAssignment}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                <Plus size={14} /> Добавить назначение
              </Button>
            </div>
            {assignmentsError && (
              <Card className="p-4 bg-red-500/10 border-red-500/30 text-sm text-red-300">{assignmentsError}</Card>
            )}
            {!assignmentsError && assignments.length === 0 && (
              <Card className="p-8 bg-[#131924] border-[#212b3d] text-center text-slate-500">
                Назначений пока нет.
              </Card>
            )}
            <div className="space-y-2">
              {assignments.map((a) => {
                const st = ASSIGNMENT_STATUS[a.status] || { label: a.status, cls: "" };
                return (
                  <Card key={a.id} data-testid={`assignment-card-${a.id}`}
                    className="p-4 bg-[#131924] border-[#212b3d] flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-medium text-slate-100 truncate">
                          {a.crm_worker?.name || `Сотрудник ${String(a.worker_id || "").slice(-6)}`}
                        </div>
                        <Chip className={st.cls}>{st.label}</Chip>
                        <Chip className="bg-slate-500/15 text-slate-300 border-slate-500/30">{a.role}</Chip>
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {fmtBerlinDateTime(a.starts_at)} → {fmtBerlinDateTime(a.ends_at)}
                      </div>
                    </div>
                    {a.status === "active" && (
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => startEditAssignment(a)}
                          className="bg-transparent border-[#212b3d] hover:bg-[#1c2434]"><Pencil size={13} /></Button>
                        <Button size="sm" variant="outline" onClick={() => cancelAssignment(a)}
                          className="bg-transparent border-[#212b3d] hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/40">
                          <X size={13} />
                        </Button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="commercial" className="mt-6">
            <CommercialPanel data={commercial} error={commercialError} reload={load} />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{editingAssignment ? "Изменить назначение" : "Новое назначение"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Сотрудник</Label>
              <Select value={assignForm.worker_id} onValueChange={(v) => setAssignForm({ ...assignForm, worker_id: v })}
                disabled={!!editingAssignment}>
                <SelectTrigger data-testid="assignment-worker-select" className="bg-[#0f1521] border-[#212b3d] disabled:opacity-60">
                  <SelectValue placeholder="Выберите сотрудника" />
                </SelectTrigger>
                <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
                  {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Роль</Label>
              <Input data-testid="assignment-role-input" value={assignForm.role}
                onChange={(e) => setAssignForm({ ...assignForm, role: e.target.value })}
                className="bg-[#0f1521] border-[#212b3d]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Начало</Label>
                <Input type="datetime-local" data-testid="assignment-starts-input" value={assignForm.starts_at}
                  onChange={(e) => setAssignForm({ ...assignForm, starts_at: e.target.value })}
                  className="bg-[#0f1521] border-[#212b3d]" />
              </div>
              <div>
                <Label>Окончание</Label>
                <Input type="datetime-local" data-testid="assignment-ends-input" value={assignForm.ends_at}
                  onChange={(e) => setAssignForm({ ...assignForm, ends_at: e.target.value })}
                  className="bg-[#0f1521] border-[#212b3d]" />
              </div>
            </div>
            <div className="text-[11px] text-slate-500">Время указывается по Берлину.</div>
          </div>
          <DialogFooter>
            <Button data-testid="save-assignment-button" onClick={saveAssignment}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommercialPanel({ data, error, reload }) {
  if (error) return <Card className="p-4 bg-red-500/10 border-red-500/30 text-red-300 text-sm">{error}</Card>;
  if (!data) return <Card className="p-6 bg-[#131924] border-[#212b3d] text-slate-500">Загрузка коммерческого процесса…</Card>;
  const risk = data.risk || {};
  const steps = [
    ["auftrag", "Auftrag"],
    ["vertrag_unterschrieben", "Vertrag"],
    ["anzahlung_bezahlt", "Anzahlung"],
    ["arbeit_freigegeben", "Freigabe"],
    ["in_arbeit", "Arbeit"],
    ["abnahme_erfolgt", "Abnahme"],
    ["rechnung_offen", "Rechnung"],
    ["bezahlt", "Zahlung"],
  ];
  const current = data.workflow?.current;
  return (
    <div className="space-y-4">
      <Card className="p-5 bg-[#131924] border-[#212b3d]">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {steps.map(([key, label], idx) => {
            const active = key === current;
            const done = steps.findIndex((s) => s[0] === current) > idx;
            return (
              <div key={key} className={`px-3 py-2 rounded-lg border text-xs font-medium ${active ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : done ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-300" : "bg-[#0f1521] border-[#212b3d] text-slate-500"}`}>
                {label}
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <MiniRisk label="Vertrag" value={risk.contract_signed ? "подписан" : "нет"} good={risk.contract_signed} />
          <MiniRisk label="Anzahlung" value={risk.deposit_paid ? "оплачена" : risk.deposit_required ? "ожидает" : "не нужна"} good={risk.deposit_paid || !risk.deposit_required} />
          <MiniRisk label="Открыто" value={fmtEur(risk.open_sum)} />
          <MiniRisk label="Срок" value={fmtDate(risk.next_due_date)} warn={risk.overdue} />
          <MiniRisk label="Mahnstufe" value={risk.dunning_level || 0} warn={(risk.dunning_level || 0) > 0} />
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Section title="Vertrag" icon={FileSignature} empty="Договор по объекту ещё не связан.">
          {(data.contracts || []).map((c) => <Row key={c.id} title={c.title} meta={c.client_id} chip={CONTRACT_STATUS[c.status]?.label || c.status} cls={CONTRACT_STATUS[c.status]?.cls} />)}
        </Section>
        <Section title="Zahlungsplan" icon={Wallet} empty="Платёжный план ещё не создан.">
          {(data.payment_stages || []).map((s) => (
            <Row key={s.id} title={`${s.title} · ${fmtEur(s.amount)}`} meta={fmtDate(s.due_date)}
              chip={PAYMENT_STAGE_STATUS[s.status]?.label || s.status} cls={PAYMENT_STAGE_STATUS[s.status]?.cls}
              action={s.status !== "paid" ? { label: "Bezahlt", onClick: () => commercialAction(`/payment-stages/${s.id}/paid`, reload) } : null} />
          ))}
        </Section>
        <Section title="Rechnungen" icon={Wallet} empty="Счетов ещё нет.">
          {(data.invoices || []).map((i) => (
            <Row key={i.id} title={`${i.invoice_number} · ${fmtEur(i.brutto)}`} meta={fmtDate(i.due_date)}
              chip={INVOICE_STATUS[i.status]?.label || i.status} cls={INVOICE_STATUS[i.status]?.cls}
              action={i.status !== "paid" ? { label: "Aktionen", onClick: () => invoiceAction(i.id, reload) } : null} />
          ))}
        </Section>
        <Section title="Abnahmen / Mahnungen" icon={TrendingUp} empty="Приёмок и действий пока нет.">
          {(data.abnahmen || []).map((a) => <Row key={a.id} title={a.type} meta={fmtDate(a.date)} chip={a.status} />)}
          {(data.actions || []).map((a) => <Row key={a.id} title={a.type} meta={fmtDateTime(a.created_at)} chip="Aktion" />)}
        </Section>
      </div>
    </div>
  );
}

function MiniRisk({ label, value, good = false, warn = false }) {
  return (
    <div className={`rounded-lg border p-3 ${warn ? "border-red-500/30 bg-red-500/10" : good ? "border-emerald-500/25 bg-emerald-500/10" : "border-[#212b3d] bg-[#0f1521]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-semibold text-slate-100">{value || "—"}</div>
    </div>
  );
}

function Section({ title, icon: Icon, empty, children }) {
  const items = Array.isArray(children) ? children.filter(Boolean) : [children].filter(Boolean);
  return (
    <Card className="p-5 bg-[#131924] border-[#212b3d]">
      <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Icon size={16} className="text-amber-400" /> {title}</h3>
      <div className="space-y-2">{items.length ? items : <div className="text-sm text-slate-500">{empty}</div>}</div>
    </Card>
  );
}

async function commercialAction(path, reload) {
  try {
    await api.post(path);
    toast.success("Aktualisiert");
    reload();
  } catch (e) {
    toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
  }
}

async function invoiceAction(invoiceId, reload) {
  const action = window.prompt("Aktion: paid / erinnerung / mahnung / eskalation", "erinnerung");
  if (!action) return;
  try {
    if (action === "paid") {
      await api.post(`/invoices/${invoiceId}/paid`);
    } else {
      await api.post(`/invoices/${invoiceId}/actions`, { type: action, notes: "" });
    }
    toast.success("Aktualisiert");
    reload();
  } catch (e) {
    toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
  }
}

function Row({ title, meta, chip, cls, action }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-md bg-[#0f1521] border border-[#212b3d] p-3 text-sm">
      <div className="min-w-0">
        <div className="text-slate-100 font-medium truncate">{title}</div>
        <div className="text-[11px] text-slate-500 truncate">{meta || "—"}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {chip && <Chip className={cls || "bg-slate-500/15 text-slate-300 border-slate-500/30"}>{chip}</Chip>}
        {action && <Button size="sm" variant="outline" onClick={action.onClick} className="bg-transparent border-[#212b3d] hover:bg-[#1c2434]">{action.label}</Button>}
      </div>
    </div>
  );
}

function StatBox({ label, value, accent = "amber" }) {
  const accents = {
    amber:   "text-amber-400",
    blue:    "text-blue-400",
    emerald: "text-emerald-400",
    red:     "text-red-400",
  };
  return (
    <div className="rounded-lg border border-[#212b3d] bg-[#131924] p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`font-display font-bold text-xl ${accents[accent]}`}>{value}</div>
    </div>
  );
}

function InfoRow({ k, v }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500 mb-0.5">{k}</div>
      <div className="text-slate-200">{v || "—"}</div>
    </div>
  );
}
